/**
 * The one refusal a person can actually clear, said so they can clear it.
 *
 * ⭐ The bug this closes. Somebody uploading a spreadsheet pressed "Save table"
 * and got a toast reading:
 *
 *     Could not save that table — Analytics could not read this on your behalf:
 *     data-studio refused the request (401). Your analytics session is valid, so
 *     this is not your sign-in.
 *
 * Every clause true, the whole unusable. The 401 was a foregone conclusion:
 * analytics could not get a Data Studio token for this person, so it forwarded
 * its own, and Data Studio verifies the `app` claim and must refuse one. The
 * status described analytics' fallback, not their problem — and the advice that
 * followed pointed at a console they cannot open.
 *
 * The real reason lives one layer up, in why Identity Studio would not mint the
 * token, and the usual answer is the simplest possible one: **this browser is
 * not signed into the suite as the account that owns the data.** Analytics' own
 * sign-in form cannot set the shared suite cookie (Identity Studio sets it on
 * the connection it answers, and for that form the connection is the backend's),
 * so a password sign-in here signs somebody into Analytics alone. The same
 * refusal comes back when the suite session belongs to a different account.
 *
 * One press fixes both, and this is the press.
 *
 * ⚠️ It opens for `sign_in_to_suite` and `wrong_account` and NOTHING else. A
 * missing cross-application grant (`ask_admin`) and a target that refused a
 * properly delegated token (`retry`) are not this person's to fix; offering them
 * a sign-in would send them round a loop that ends where it started, having told
 * them the fix was theirs. Those keep the sources-screen banner they already
 * have.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { UserRoundX } from 'lucide-react';

import { useAuth } from '../../../hooks/useAuth';
import { setSiblingRefusalHandler } from '../../../lib/api';
import {
  isAccountMismatch,
  siblingRefusalOf,
  type SiblingRefusal,
} from '../../../lib/sibling-refusal';
import { Button, Modal, ModalContent, errorMessage } from '../../../ui';

/** A board with fifteen tiles refuses fifteen times; that is one problem. */
function refusalKey(refusal: SiblingRefusal): string {
  return `${refusal.target}:${refusal.remedy}:${refusal.account}`;
}

export function AccountMismatchGate() {
  const { signIn } = useAuth();
  const [refusal, setRefusal] = useState<SiblingRefusal | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  /**
   * What has already been answered, and must not re-open on its own.
   *
   * Dismissal has to survive the next refusal or this is a modal that cannot be
   * closed: the queries behind it keep running, and every one of them reports.
   * A ref rather than state — re-rendering on a dedup decision is what would
   * make the loop it prevents.
   */
  const dismissed = useRef<Set<string>>(new Set());

  useEffect(() => {
    setSiblingRefusalHandler((error) => {
      const next = siblingRefusalOf(error);
      if (!next || !isAccountMismatch(next)) return;
      if (dismissed.current.has(refusalKey(next))) return;
      // Last writer wins only while nothing is shown: replacing the dialog a
      // person is reading, because a background query answered, is worse than
      // showing the first of two identical refusals.
      setRefusal((shown) => shown ?? next);
    });
    return () => setSiblingRefusalHandler(null);
  }, []);

  const close = useCallback(() => {
    setRefusal((shown) => {
      if (shown) dismissed.current.add(refusalKey(shown));
      return null;
    });
    setSignInError(null);
  }, []);

  const signInToSuite = useCallback(async () => {
    if (!refusal) return;
    setSignInError(null);
    setSigningIn(true);
    try {
      // ⭐ AG-1989 — this re-enters the SILENT leg, and nothing of Identity Studio
      // renders. `signIn` navigates to `/app/{key}/sso/continue`, a redirect endpoint
      // that answers 302 either way; with no suite session the callback now lands on
      // OUR OWN `/login`, whose form collects the suite cookie itself via the plant
      // hop. The sentence here used to say the hosted page was the only place a suite
      // session could be established — true when written, false since the plant hop.
      // On success the browser navigates away and nothing below runs.
      await signIn(window.location.pathname + window.location.search);
    } catch (err) {
      setSignInError(errorMessage(err, 'Could not reach the sign-in service.'));
      setSigningIn(false);
    }
  }, [refusal, signIn]);

  if (!refusal) return null;

  const who = refusal.target || 'the application that owns your data';
  const canSignIn = refusal.remedy === 'sign_in_to_suite';

  return (
    <Modal open onOpenChange={(open) => !open && close()}>
      <ModalContent
        size="md"
        title={canSignIn ? 'Sign in with the same account' : 'This account cannot read that data'}
        description={`Analytics and ${who} are separate applications in one suite.`}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={close}>
              Not now
            </Button>
            {canSignIn && (
              <Button size="sm" loading={signingIn} onClick={() => void signInToSuite()}>
                {signingIn ? 'Taking you to sign in…' : 'Sign in across the suite'}
              </Button>
            )}
          </>
        }
      >
        <div className="flex gap-3">
          <span
            className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-surface-subtle text-fg-muted"
            aria-hidden
          >
            <UserRoundX className="size-4" />
          </span>
          <div className="min-w-0 space-y-3">
            {/* The backend already wrote this for a person, and it is the only
                place that knows which account and which application. Rewriting
                it here would mean maintaining the sentence twice. */}
            <p className="text-[0.8125rem] leading-relaxed text-fg-secondary">{refusal.detail}</p>
            {canSignIn && (
              <p className="text-xs leading-relaxed text-fg-muted">
                Signing in on this screen alone does not sign you into {who}. Use the suite
                sign-in and both applications end up as the same account — then your tables
                save and your charts read normally.
              </p>
            )}
            {signInError && (
              <p className="text-xs leading-relaxed text-danger" role="alert">
                {signInError}
              </p>
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
